import {
  useCallback, useEffect, useState,
} from 'react';

type Options = {
  enabled?: boolean;
  rootMargin?: string;
};

/**
 * Defers an image URL until the target element is near the viewport.
 * Used for long people lists so dozens of avatars do not download at once.
 */
export function useLazyImageSrc(
  src: string | undefined,
  options: Options = {},
) {
  const { enabled = true, rootMargin = '400px 0px' } = options;
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [activeSrc, setActiveSrc] = useState(() => (
    enabled ? '' : (src ?? '')
  ));

  const ref = useCallback((element: HTMLElement | null) => {
    setNode(element);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setActiveSrc(src ?? '');
      return undefined;
    }

    if (!src) {
      setActiveSrc('');
      return undefined;
    }

    if (!node) {
      return undefined;
    }

    setActiveSrc('');

    if (typeof IntersectionObserver === 'undefined') {
      setActiveSrc(src);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActiveSrc(src);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, node, rootMargin, src]);

  return { ref, src: activeSrc };
}
